import { FC, ReactNode, useMemo, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbIcons } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    LearnMoreFooter,
} from '@/shared';
import { useMemoStore } from '@/stores';
import { Memo, MemoDocumentType } from '@/types';
import { formatDate } from '@/utilities';

interface MemoTemplatesDropdownProps {
    memos: Memo[];
    children: ReactNode;
}

const MemoTemplatesDropdown: FC<MemoTemplatesDropdownProps> = ({ memos, children }): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const { hasMinimumPlan } = useEntitlements();
    const { createMemo } = useMemoStore();
    const createAccess = useWorkspaceResourceCreateAccess('memos', memos?.length ?? 0);

    const createMemoAndNavigate = (input: Partial<Memo>) => {
        if (createAccess.disabled) {
            return;
        }

        setDropdownOpen(false);
        void createMemo(input).then((memo) => {
            if (memo?.id) {
                navigate(`/memos/${memo.id}`);
            }
        });
    };

    const memoTemplates = useMemo(
        () => memos.filter((memo) => memo.documentType === MemoDocumentType.TEMPLATE),
        [memos],
    );
    const filteredMemoTemplates = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();

        if (!normalizedSearch) {
            return memoTemplates;
        }

        return memoTemplates.filter((memo) => memo.title?.toLocaleLowerCase().includes(normalizedSearch));
    }, [memoTemplates, search]);

    const handleDropdownOpenChange = (nextOpen: boolean) => {
        setDropdownOpen(nextOpen);

        if (!nextOpen) {
            setSearch('');
        }
    };

    return (
        <Dropdown
            disabled={createAccess.disabled}
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
            placement='bottomRight'
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    actions={
                        <DropdownPanelMenu selectable={false}>
                            <Menu.Item
                                disabled={createAccess.disabled}
                                icon={<TbIcons size='1rem' />}
                                key='blank-template'
                                onClick={() =>
                                    createMemoAndNavigate({
                                        documentType: MemoDocumentType.TEMPLATE,
                                        title: 'Untitled Memo',
                                    })
                                }
                            >
                                Blank Template
                            </Menu.Item>
                        </DropdownPanelMenu>
                    }
                    footer={<LearnMoreFooter topic='Memo templates' variant='dropdown' />}
                    search={{
                        placeholder: 'Search Memo Templates...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.5rem' }}
                    width={16 * 28}
                >
                    <DropdownMenuLabel icon={<TbIcons size='0.875rem' />}>
                        {filteredMemoTemplates.length} Memo Template
                        {filteredMemoTemplates.length === 1 ? '' : 's'}
                    </DropdownMenuLabel>
                    <DropdownPanelMenu selectable={false}>
                        {filteredMemoTemplates.length ? (
                            filteredMemoTemplates.map((memo) => (
                                <Menu.Item
                                    disabled={createAccess.disabled || !hasMinimumPlan('plus')}
                                    key={memo.id}
                                    onClick={() =>
                                        createMemoAndNavigate({
                                            body: memo.body ?? '',
                                            title: memo.title ?? 'Untitled Memo',
                                        })
                                    }
                                >
                                    <DropdownOptionContent
                                        avatar={{ icon: <TbIcons size='1rem' /> }}
                                        metadata={[
                                            <span key='last-edited'>
                                                Last edited {memo.updatedAt ? formatDate(memo.updatedAt) : 'recently'}
                                            </span>,
                                        ]}
                                        title={memo.title ?? 'Untitled Memo'}
                                    />
                                </Menu.Item>
                            ))
                        ) : (
                            <EmptySection
                                description={
                                    search
                                        ? 'No memo templates match your search.'
                                        : "You don't have any memo templates yet."
                                }
                                height='12.5rem'
                                icon={<TbIcons size='75%' style={{ color: token.colorTextTertiary }} />}
                                label={search ? 'No Templates Found' : 'No Memo Templates'}
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default MemoTemplatesDropdown;
